import { useDocumentExport } from "@lib/hooks/useDocumentExport";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

// Fires a mutation that returns a DocumentExport, then polls for completion.
// When complete, opens the Google Doc URL in a new tab.
export interface ExportButtonProps {
  label?: string;
  disabled?: boolean;
  className?: string;
  onStart: () => Promise<{ documentExportId: string | null; errors: string[] }>;
}

export function ExportButton({
  label = "Export to Google Docs",
  disabled = false,
  className,
  onStart,
}: ExportButtonProps) {
  const [exportId, setExportId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { exportRow } = useDocumentExport(exportId);

  // Once completed, auto-open the Doc URL (once).
  useEffect(() => {
    if (exportRow?.status === "completed" && exportRow.googleDocUrl) {
      window.open(exportRow.googleDocUrl, "_blank", "noopener,noreferrer");
      setExportId(null);
    } else if (exportRow?.status === "failed") {
      setErrors([exportRow.errorMessage || "Export failed"]);
      setExportId(null);
    }
  }, [exportRow?.status, exportRow?.googleDocUrl, exportRow?.errorMessage]);

  const handleClick = async () => {
    setErrors([]);
    setBusy(true);
    try {
      const result = await onStart();
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else if (result.documentExportId) {
        setExportId(result.documentExportId);
      }
    } finally {
      setBusy(false);
    }
  };

  const isRunning =
    busy || (exportRow && exportRow.status !== "completed" && exportRow.status !== "failed");
  const baseClasses =
    "inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50";

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || !!isRunning}
        className={className ?? baseClasses}
        data-export-button
      >
        {isRunning ? (
          "Exporting…"
        ) : (
          <>
            <ExternalLink size={12} />
            {label}
          </>
        )}
      </button>
      {errors.length > 0 && (
        <p className="text-xs text-red-600" data-export-error>
          {errors[0]}
        </p>
      )}
    </div>
  );
}
